import { attendanceCounts, sessionAttendanceCounts, sessionAttendanceTaken, type BusinessSession } from "@teamna/shared";
import { formatTime } from "@teamna/web-client/lib/format.ts";

/**
 * Where attendance is for one session today. The Home "Today" card and the Calendar's Day view
 * both read it, so a session says the same thing in both places.
 *
 * - `cancelled`: the session does not run.
 * - `empty`: nobody is booked and nobody came yet. Attendance stays open: people can come with no
 *   booking, and the attendance screen adds them (Did not book, Someone else came).
 * - `taken`: every booked person has a mark, or, with nobody booked, the coach saved who came.
 * - `partial`: some have a mark (often the coach started on the floor).
 * - `missed`: the session ended and nobody has a mark.
 * - `now`: the session runs now and nobody has a mark yet.
 * - `later`: the session starts later today.
 */
export type TodayState = "cancelled" | "empty" | "taken" | "partial" | "missed" | "now" | "later";

export type TodayProgress = {
  state: TodayState;
  /** Booked people on the register. */
  total: number;
  /** Booked people with a mark. */
  checked: number;
  /** Everyone who came: booked and marked present, plus the names that wait for a parent. */
  present: number;
  absent: number;
};

export function todayProgress(session: BusinessSession, now: number): TodayProgress {
  const roster = session.roster;
  const booked = attendanceCounts(roster);
  const { present, absent } = sessionAttendanceCounts(session);
  const counts = { total: roster.length, checked: booked.present + booked.absent, present, absent };
  let state: TodayState;
  if (session.status !== "scheduled") state = "cancelled";
  else if (sessionAttendanceTaken(session)) state = "taken";
  else if (roster.length === 0) state = "empty";
  else if (counts.checked > 0) state = "partial";
  else if (Date.parse(session.endsAt) <= now) state = "missed";
  else if (Date.parse(session.startsAt) <= now) state = "now";
  else state = "later";
  return { state, ...counts };
}

/** The words for a session's attendance: "4 of 11 checked", "Not taken yet". */
export function todayStatusText(progress: TodayProgress, session: BusinessSession, timezone: string): string {
  switch (progress.state) {
    case "cancelled":
      return "Cancelled";
    case "empty":
      return "Nobody booked";
    case "taken":
      return `Taken: ${progress.present} came, ${progress.absent} absent`;
    case "partial":
      return `${progress.checked} of ${progress.total} checked`;
    case "missed":
      return "Not taken yet";
    case "now":
      return `On now, ${progress.total} booked`;
    case "later":
      return `Starts at ${formatTime(session.startsAt, timezone)}`;
  }
}

/**
 * Attendance on a session of the Calendar's day list (`OwnerSessionCard`), from the session's
 * start: "Taken: 9 came, 0 absent", "4 of 11 checked", or "Attendance not taken" once it ended.
 * Null before the start, for a cancelled session, and for one nobody booked and nobody came.
 */
export function attendanceBadge(progress: TodayProgress, session: BusinessSession, timezone: string): { label: string; tone: "confirmed" | "late" | "quiet" } | null {
  switch (progress.state) {
    case "taken":
      return { label: todayStatusText(progress, session, timezone), tone: "confirmed" };
    case "partial":
      return { label: todayStatusText(progress, session, timezone), tone: "quiet" };
    case "missed":
      return { label: "Attendance not taken", tone: "late" };
    default:
      return null;
  }
}

/**
 * The counts over the day, the same on Home and on the Day view: taken, still to take (on now,
 * partly marked or ended with no marks), and later.
 */
export function todayCounts(progress: readonly TodayProgress[]): { taken: number; toTake: number; later: number } {
  const count = (states: TodayState[]) => progress.filter((row) => states.includes(row.state)).length;
  return { taken: count(["taken"]), toTake: count(["now", "partial", "missed"]), later: count(["later"]) };
}

/**
 * Every session that runs can open the attendance screen, also with nobody booked (2026-10-21):
 * people come with no booking, and the screen adds them from the level's Did not book list.
 */
export function canTakeAttendance(progress: TodayProgress): boolean {
  return progress.state !== "cancelled";
}

/** The label of the button that opens the attendance screen, on the session panel and on Home. */
export function attendanceButtonLabel(taken: boolean): string {
  return taken ? "See or change attendance" : "Take attendance";
}
